import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

export function useDriveConnection(isLocalMode) {
  const [driveConnected, setDriveConnected] = useState(false);
  const [checkingDrive, setCheckingDrive] = useState(true);
  const callbackHandled = useRef(false);

  useEffect(() => {
    if (isLocalMode) {
      setDriveConnected(true);
      setCheckingDrive(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true' && !callbackHandled.current) {
      callbackHandled.current = true;
      toast.success('Google Drive connected successfully!');
      setDriveConnected(true);
      setCheckingDrive(false);
      window.history.replaceState({}, '', '/library');
    } else if (params.get('drive_error') === 'true') {
      toast.error('Failed to connect Google Drive');
      setCheckingDrive(false);
      window.history.replaceState({}, '', '/library');
    } else if (!callbackHandled.current) {
      checkDriveConnection();
    }
  }, []);

  // Listen for Drive OAuth popup completion
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'drive_connected') {
        toast.success('Google Drive connected successfully!');
        setDriveConnected(true);
        setCheckingDrive(false);
      }
    };
    const handleStorage = (event) => {
      if (event.key === 'drive_just_connected' && event.newValue === 'true') {
        localStorage.removeItem('drive_just_connected');
        toast.success('Google Drive connected successfully!');
        setDriveConnected(true);
        setCheckingDrive(false);
      }
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  async function checkDriveConnection() {
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      const response = await axios.get(`${BACKEND_URL}/api/oauth/drive/status`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data.connected) {
        setDriveConnected(true);
      }
      setCheckingDrive(false);
    } catch {
      setCheckingDrive(false);
    }
  }

  async function handleConnectDrive() {
    const width = 500, height = 600;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    let popup;
    try {
      popup = window.open('about:blank', 'driveAuth', `width=${width},height=${height},left=${left},top=${top}`);
    } catch (err) {
      // Popup blocker triggered — we fall back to same-window redirect below
      console.debug('window.open blocked, falling back to same-window:', err?.message);
    }

    if (!popup || popup.closed) {
      // Popup blocked — fall back to same-window redirect
      try {
        const sessionToken = localStorage.getItem('auth_hint');
        const response = await axios.get(`${BACKEND_URL}/api/oauth/drive/connect?origin=${encodeURIComponent(window.location.origin)}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        window.location.href = response.data.authorization_url;
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
        console.error('Drive connect fallback error:', msg);
        toast.error(`Failed to start Drive connection: ${msg}`);
      }
      return;
    }

    try {
      const sessionToken = localStorage.getItem('auth_hint');
      const response = await axios.get(`${BACKEND_URL}/api/oauth/drive/connect?origin=${encodeURIComponent(window.location.origin)}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      popup.location.href = response.data.authorization_url;

      // Poll drive status in case popup communication fails (e.g. incognito)
      const pollInterval = setInterval(async () => {
        try {
          if (popup.closed) {
            clearInterval(pollInterval);
            // Check one more time after popup closes
            const statusResp = await axios.get(`${BACKEND_URL}/api/oauth/drive/status`, {
              headers: { Authorization: `Bearer ${sessionToken}` }
            });
            if (statusResp.data.connected) {
              toast.success('Google Drive connected successfully!');
              setDriveConnected(true);
              setCheckingDrive(false);
            }
            return;
          }
          const statusResp = await axios.get(`${BACKEND_URL}/api/oauth/drive/status`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          if (statusResp.data.connected) {
            clearInterval(pollInterval);
            toast.success('Google Drive connected successfully!');
            setDriveConnected(true);
            setCheckingDrive(false);
            try { popup.close(); } catch (err) {
              // Popup may already be closed by the user
              console.debug('popup.close failed (already closed):', err?.message);
            }
          }
        } catch (err) {
          // Single poll failure is fine — next interval retries
          console.debug('Drive status poll failed (will retry):', err?.message);
        }
      }, 2000);

      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(pollInterval), 300000);
    } catch (err) {
      if (popup) popup.close();
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      console.error('Drive connect error:', status, msg);
      toast.error(`Failed to start Drive connection: ${msg}`);
    }
  }

  return { driveConnected, checkingDrive, handleConnectDrive };
}
